/**
 * StackedAvatars component for displaying two overlapping user avatars.
 * Used for "Ask on Behalf" questions where the creator avatar is larger (behind)
 * and the beneficiary avatar is smaller (in front, bottom-right).
 *
 * AIDEV-NOTE: Created for QuestionCard redesign to show creator/beneficiary relationship
 */

import React from 'react';
import { UserAvatar } from '@components/UserAvatar';
import { cn } from '@lib/utils';

export interface StackedAvatarsProps {
  /** Creator (person who asked the question) - shown as larger avatar behind */
  creator: {
    imageUrl?: string | null;
    fullName: string;
  };
  /** Beneficiary (person the question was asked for) - shown as smaller avatar in front */
  beneficiary: {
    imageUrl?: string | null;
    fullName: string;
  };
  /** Click handler for the creator avatar */
  onCreatorClick?: (e: React.MouseEvent) => void;
  /** Click handler for the beneficiary avatar */
  onBeneficiaryClick?: (e: React.MouseEvent) => void;
  /** Additional CSS classes for the container */
  className?: string;
}

export const StackedAvatars: React.FC<StackedAvatarsProps> = ({
  creator,
  beneficiary,
  onCreatorClick,
  onBeneficiaryClick,
  className,
}) => {
  return (
    <div className={cn('relative w-16 h-16', className)}>
      {/* Creator avatar - larger, behind */}
      <button
        type="button"
        className="absolute top-0 left-0 w-14 h-14 rounded-full transition-all"
        onClick={onCreatorClick}
        aria-label={`View ${creator.fullName}'s profile`}
      >
        <UserAvatar
          imageUrl={creator.imageUrl}
          fullName={creator.fullName}
          className="w-14 h-14 cursor-pointer transition-all"
          size="xl"
          alt={creator.fullName}
        />
      </button>

      {/* Beneficiary avatar - smaller, overlapping at bottom-right */}
      <button
        type="button"
        className="absolute bottom-0 right-0 z-10 rounded-full focus:outline-hidden transition-all"
        onClick={onBeneficiaryClick}
        aria-label={`View ${beneficiary.fullName}'s profile`}
      >
        <UserAvatar
          imageUrl={beneficiary.imageUrl}
          fullName={beneficiary.fullName}
          className="w-8 h-8 cursor-pointer ring-2 ring-white hover:ring-gray-200 transition-all"
          size="md"
          alt={beneficiary.fullName}
        />
      </button>
    </div>
  );
};
